import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

/**
 * Client Types:
 * - ADMIN: Full admin panel access (determined by user_roles table)
 * - TEST_LIFETIME: Mimics Lifetime access without payment (for testing)
 * - TEST_MONTHLY: Mimics Monthly access without payment (for testing)
 * - LIFETIME: Paid lifetime - full access to all content
 * - MONTHLY: Paid monthly - gradual unlock (1st module, then monthly or completion-based unlock)
 * - FREEMIUM: Registered but not paid - only Academy 1, Module 1, Class 1
 */
export type ClientType = 
  | 'admin' 
  | 'test_lifetime' 
  | 'test_monthly' 
  | 'lifetime' 
  | 'monthly' 
  | 'freemium';

export interface ClientAccessInfo {
  clientType: ClientType;
  clientId: string | null;
  plan: string | null;
  hasAccess: boolean;
  paid: boolean;
  subscriptionStatus: string | null;
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  daysRemaining: number | null;
  isLifetimeAccess: boolean;
  isTestAccount: boolean;
  isTrialing: boolean;
  isGracePeriod: boolean;
}

interface CompletedModule {
  academyId: string;
  moduleNumber: number;
}

export interface ClientAccessResult {
  loading: boolean;
  clientInfo: ClientAccessInfo;
  
  // Access check methods
  canAccessAcademy: (academyId: string) => boolean;
  canAccessModule: (academyId: string, moduleNumber: number, completedModules?: CompletedModule[]) => boolean;
  canAccessClass: (academyId: string, moduleNumber: number, classNumber: number) => boolean;
  isContentFree: (academyId: string, moduleNumber: number, classNumber?: number) => boolean;
  
  // Helper flags
  showSubscriptionWall: boolean;
  showUpgradeBanner: boolean;
  
  // Refresh
  refreshClientInfo: () => Promise<void>;
}

// Free content: Academy 1, Module 1, Class 1
const FREE_ACADEMY_ID = 'faceless-content';
const FREE_MODULE_NUMBER = 1;
const FREE_CLASS_NUMBER = 1;

const defaultClientInfo: ClientAccessInfo = {
  clientType: 'freemium',
  clientId: null,
  plan: null,
  hasAccess: false,
  paid: false,
  subscriptionStatus: null,
  subscriptionStart: null,
  subscriptionEnd: null,
  daysRemaining: null,
  isLifetimeAccess: false,
  isTestAccount: false,
  isTrialing: false,
  isGracePeriod: false,
};

export const useClientAccess = (): ClientAccessResult => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clientInfo, setClientInfo] = useState<ClientAccessInfo>(defaultClientInfo);

  const fetchClientInfo = useCallback(async () => {
    if (!user) {
      setClientInfo(defaultClientInfo);
      setLoading(false);
      return;
    }

    // Admin check first
    if (isAdmin) {
      setClientInfo({
        ...defaultClientInfo,
        clientType: 'admin',
        plan: 'Admin',
        hasAccess: true,
        paid: true,
        isLifetimeAccess: true,
      });
      setLoading(false);
      return;
    }

    // Always check subscription status with Stripe on every login/load
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.access_token) {
        const { error: syncError } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
        });
        if (syncError) {
          console.warn('Subscription sync warning:', syncError.message);
        }
      }
    } catch (syncErr) {
      console.warn('Failed to sync subscription status:', syncErr);
    }

    try {
      let { data: client } = await supabase
        .from('clients')
        .select('id, plan, has_access, paid, subscription_start, subscription_end, subscription_status')
        .eq('user_id', user.id)
        .maybeSingle();

      // If no client linked by user_id, try linking an existing email-matched record
      // (e.g. admin-created clients) then refetch.
      if (!client) {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          if (sessionData?.session?.access_token) {
            await supabase.functions.invoke('link-client-to-user', {
              body: { email: user.email },
              headers: { Authorization: `Bearer ${sessionData.session.access_token}` },
            });
            const { data: relinked } = await supabase
              .from('clients')
              .select('id, plan, has_access, paid, subscription_start, subscription_end, subscription_status')
              .eq('user_id', user.id)
              .maybeSingle();
            client = relinked ?? null;
          }
        } catch (linkErr) {
          console.warn('Auto-link client failed:', linkErr);
        }
      }

      if (!client) {
        setClientInfo(defaultClientInfo);
        setLoading(false);
        return;
      }

      // Determine client type based on plan and payment status
      let clientType: ClientType = 'freemium';
      let isLifetimeAccess = false;
      let isTestAccount = false;
      let hasAccess = client.has_access === true;
      const subStatus = client.subscription_status?.toLowerCase() || '';
      const isTrialing = subStatus === 'trialing';
      const isGracePeriod = subStatus === 'past_due' || subStatus === 'grace_period';

      const plan = client.plan?.toLowerCase() || '';

      // Check for test accounts first
      if (plan === 'test_lifetime' || plan === 'test-lifetime') {
        clientType = 'test_lifetime';
        isLifetimeAccess = true;
        isTestAccount = true;
        hasAccess = true;
      } else if (plan === 'test_monthly' || plan === 'test-monthly') {
        clientType = 'test_monthly';
        isTestAccount = true;
        hasAccess = true;
      } else if (plan === 'lifetime') {
        if (client.paid && client.has_access) {
          clientType = 'lifetime';
          isLifetimeAccess = true;
        } else {
          clientType = 'freemium';
        }
      } else if (plan === 'monthly') {
        // Monthly: trialing users have access but aren't paid yet
        // Grace period users also have temporary access
        if ((client.paid && client.has_access) || isTrialing || (isGracePeriod && client.has_access)) {
          clientType = 'monthly';
        } else {
          clientType = 'freemium';
        }
      } else if (plan === 'freemium') {
        clientType = 'freemium';
        hasAccess = true;
      } else {
        if (client.paid && client.has_access) {
          clientType = 'monthly';
        } else {
          clientType = 'freemium';
        }
      }

      // Calculate days remaining for non-lifetime
      let daysRemaining: number | null = null;
      if (client.subscription_end && !isLifetimeAccess) {
        const endDate = new Date(client.subscription_end);
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      }

      setClientInfo({
        clientType,
        clientId: client.id,
        plan: client.plan,
        hasAccess,
        paid: client.paid,
        subscriptionStatus: client.subscription_status,
        subscriptionStart: client.subscription_start,
        subscriptionEnd: client.subscription_end,
        daysRemaining,
        isLifetimeAccess,
        isTestAccount,
        isTrialing,
        isGracePeriod,
      });
    } catch (error) {
      console.error('Error fetching client info:', error);
      setClientInfo(defaultClientInfo);
    }

    setLoading(false);
  }, [user, isAdmin]);

  useEffect(() => {
    if (!authLoading) {
      fetchClientInfo();
    }
  }, [authLoading, fetchClientInfo]);

  // Check if content is free (available to all)
  const isContentFree = useCallback((academyId: string, moduleNumber: number, classNumber?: number): boolean => {
    if (academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER) {
      if (classNumber !== undefined) {
        return classNumber === FREE_CLASS_NUMBER;
      }
      return true;
    }
    return false;
  }, []);

  const canAccessAcademy = useCallback((academyId: string): boolean => {
    const { clientType } = clientInfo;
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') return true;
    if (clientType === 'monthly' || clientType === 'test_monthly') return true;
    if (clientType === 'freemium') return academyId === FREE_ACADEMY_ID;
    return false;
  }, [clientInfo]);

  const canAccessModule = useCallback((academyId: string, moduleNumber: number, completedModules?: CompletedModule[]): boolean => {
    const { clientType } = clientInfo;
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') return true;

    if (clientType === 'monthly' || clientType === 'test_monthly') {
      if (moduleNumber === 1) return true;
      if (completedModules && completedModules.length > 0) {
        for (let i = 1; i < moduleNumber; i++) {
          const isCompleted = completedModules.some(m => m.academyId === academyId && m.moduleNumber === i);
          if (!isCompleted) return false;
        }
        return true;
      }
      return false;
    }

    if (clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER;
    }
    return false;
  }, [clientInfo]);

  const canAccessClass = useCallback((academyId: string, moduleNumber: number, classNumber: number): boolean => {
    const { clientType } = clientInfo;
    if (clientType === 'admin' || clientType === 'lifetime' || clientType === 'test_lifetime') return true;
    if (clientType === 'monthly' || clientType === 'test_monthly') return canAccessModule(academyId, moduleNumber);
    if (clientType === 'freemium') {
      return academyId === FREE_ACADEMY_ID && moduleNumber === FREE_MODULE_NUMBER && classNumber === FREE_CLASS_NUMBER;
    }
    return false;
  }, [clientInfo, canAccessModule]);

  const showSubscriptionWall = useMemo(() => {
    if (clientInfo.clientType === 'freemium' && clientInfo.clientId) return false;
    return clientInfo.clientType === 'freemium' && !clientInfo.clientId && !clientInfo.hasAccess;
  }, [clientInfo]);

  const showUpgradeBanner = useMemo(() => {
    const { clientType, daysRemaining } = clientInfo;
    if (clientType === 'monthly' && daysRemaining !== null && daysRemaining <= 7) return true;
    return false;
  }, [clientInfo]);

  return {
    loading: loading || authLoading,
    clientInfo,
    canAccessAcademy,
    canAccessModule,
    canAccessClass,
    isContentFree,
    showSubscriptionWall,
    showUpgradeBanner,
    refreshClientInfo: fetchClientInfo,
  };
};
